import type { PersistenceLevel } from '@/core/flux.service';
import { Store } from '@/core/flux.service';
import type { AnyProperty } from '@/utilities.types';
import _ from 'lodash';
import type { StoredTableOutputV1 } from '@/sdk/model/StoredTableOutputV1';
import type { StoredTableColumnOutputV1 } from '@/sdk/model/StoredTableColumnOutputV1';
import type { GridApi } from 'ag-grid-enterprise';
import type { ProcessedMaterializedTable } from '@/tableDefinitionEditor/tableDefinition.types';
import { type MaterializedTableOutput } from '@/tableDefinitionEditor/tableDefinition.types';
import { processMaterializedTable } from '@/tableDefinitionEditor/processedMaterializedTable.utilities';

/**
 * This store manages the state for the stored table editor, including the current table,
 * its columns, reload state, and other related editor state.
 */
export class StoredTableStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  static readonly storeName = 'sqStoredTableStore';

  initialize() {
    this.state = this.immutable({
      tableId: undefined as string | undefined,
      storedTable: undefined as StoredTableOutputV1 | undefined,
      columns: [] as StoredTableColumnOutputV1[],
      isLoadingStoredTable: false,
      name: undefined as string | undefined,
      materializedTable: undefined as ProcessedMaterializedTable | undefined,
    });
  }

  get tableId(): string | undefined {
    return this.state.get('tableId');
  }

  get storedTable(): StoredTableOutputV1 | undefined {
    return this.state.get('storedTable');
  }

  get columns(): StoredTableColumnOutputV1[] {
    return this.state.get('columns');
  }

  get isLoadingStoredTable(): boolean {
    return this.state.get('isLoadingStoredTable');
  }

  get name(): string | undefined {
    return this.state.get('name');
  }

  get agGridApi(): GridApi | undefined {
    return this.state.get('agGridApi');
  }

  get materializedTable(): ProcessedMaterializedTable | undefined {
    return this.state.get('materializedTable');
  }

  dehydrate() {
    return _.omit(this.state.serialize(), ['storedTable', 'materializedTable', 'agGridApi', 'columns', 'name']);
  }

  rehydrate(dehydratedState: AnyProperty) {
    this.state.merge(dehydratedState);
  }

  protected readonly handlers = {
    STORED_TABLE_SET_TABLE_ID: (payload: { tableId: string }) => {
      this.state.set('tableId', payload.tableId);
    },
    SET_STORED_TABLE: (payload: { storedTable: StoredTableOutputV1 }) => {
      const storedTable = payload.storedTable;
      this.state.set('storedTable', storedTable);
      this.state.set('name', storedTable.name);
      this.state.set('columns', storedTable.columns);
    },
    RESET_STORED_TABLE: () => {
      this.initialize();
    },
    STORED_TABLE_SET_IS_LOADING_STORED_TABLE: (payload: { isLoadingStoredTable: boolean }) => {
      this.state.set('isLoadingStoredTable', payload.isLoadingStoredTable);
    },
    SET_STORED_TABLE_AG_GRID_API: (payload: { agGridApi: GridApi }) => {
      this.state.set('agGridApi', payload.agGridApi);
    },
    SET_STORED_TABLE_MATERIALIZED_TABLE: (payload: { materializedTable: MaterializedTableOutput }) => {
      const processedMaterializedTable = processMaterializedTable(payload.materializedTable, this.state.get('columns'));
      this.state.set('materializedTable', processedMaterializedTable);
    },
    SET_STORED_TABLE_NAME: (payload: { name: string }) => {
      this.state.set('name', payload.name);
    },
  };
}
